import React, { useCallback, useEffect, useState } from 'react'
import { Typography, Button, Column, Container, Spinner } from 'components'
import { Trans } from '@lingui/macro'
import BiographyData from './RegisterBlocks/BiographyData'
import DocumentsData from './RegisterBlocks/DocumentsData'
import EducationData from './RegisterBlocks/EducationData'
import GeneralData from './RegisterBlocks/GeneralData'
import LineSeparator from '../../../components/LineSeparator'
import MediaData from './RegisterBlocks/MediaData'
import ProductsData from './RegisterBlocks/ProductsData'
import Row from '../../../components/Row'
import SkillsData from './RegisterBlocks/SkillsData'
import Toast from '../../../components/Toast'
import * as actions from '../../../redux/actions'
import * as routes from '../../../settings/_routesSettings'
import { useViewportInfo } from 'hooks'
import { useDispatch, useSelector } from 'react-redux'
import { ACTIVE, INCOMPLETE_ACCOUNT } from 'settings/_personSettings'
import { modalTypes } from 'containers/ModalManager/ModalManager'

const NewProfessional = ({ match, history }) => {

  const dispatch = useDispatch()
  const { role, slug } = match.params
  const isEdit = slug !== undefined
  const { isDesktop } = useViewportInfo()
  const personGeneral = useSelector(state => state.persons.person)
  const personProfile = useSelector(state => state.persons.personProfile)
  const registerBlocks = useSelector(state => state.registerBlocks)
  const errorList = useSelector(state => state.async.errorList)
  const submitSuccess = useSelector(state => state.async.success)
  const [error, setError] = useState([])
  const [loadingData, setLoadingData] = useState(isEdit)
  const [loadingSubmit, setLoadingSubmit] = useState(null)
  const [canSave, setCanSave] = useState(false)

  const blocksToCheck = [
    'generalData',
    'biographyData',
    'educationData',
    'documentsData',
    'mediaData',
    'productsData',
    'skillsData'
  ]

  const submitTypes = {
    SAVE: 'SAVE',
    PUBLISH: 'PUBLISH'
  }

  // Preparar dados para salvamento
  const generatePersonData = (status) => {

    /**
     * This method verifies if the block is on the blockList,
     * if it is, the method treat data (if needed) and then
     * spread it on postData
     */

    let postData = {}

    const cleanCpf = registerBlocks.generalData.data.cpf ? {
      cpf: registerBlocks.generalData.data.cpf.replace('.', '').replace('.', '').replace('-', '')
    } : {}

    const cleanCellphone = registerBlocks.generalData.data.cellphone_number ? {
      cellphone_number: registerBlocks.generalData.data.cellphone_number.replace('+', '')
    } : {}

    const cleanPhone = registerBlocks.generalData.data.phone_number ? {
      phone_number: registerBlocks.generalData.data.phone_number.replace('+', '')
    } : {}

    postData = {
      ...postData,
      ...registerBlocks.generalData.data,
      ...cleanCellphone,
      ...cleanPhone,
      ...cleanCpf,
      documents: registerBlocks.documentsData.data.documents,
      bio: registerBlocks.biographyData.data.biography,
      ...registerBlocks.mediaData.data,
      skills: registerBlocks.skillsData.data.skills,
      subspecialties: registerBlocks.subspecialtiesData.data.subspecialties,
      education: registerBlocks.educationData.data.education,
      profile_role: role,
      status_code: status
    }

    let formatedAppointments = []
    if (registerBlocks.productsData.data.products.length > 0) {
      if (registerBlocks.productsData.data.products) {
        let appointments = [...registerBlocks.productsData.data.products]

        for (let i = 0; i < appointments.length; i++) {
          const product = appointments[i]
          let formatedItem

          if (product.price) {
            formatedItem = {
              ...product,
              price: parseFloat(product.price.replace(',', '.')),
            }
          }

          formatedAppointments.push(formatedItem)
        }

        postData = {
          ...postData,
          appointments: formatedAppointments,
        }
      }
    }

    return postData
  }

  // Salvar ou publicar informações do profissional
  const onSubmit = async (submitType) => {
    setError(null)
    setLoadingSubmit(submitType)

    const status = submitType === submitTypes.PUBLISH ? ACTIVE : INCOMPLETE_ACCOUNT
    const personData = generatePersonData(status)

    if (isEdit) {
      await dispatch(actions.changePerson(slug, role, personData))
    } else {
      await dispatch(actions.setProfessional(personData))
    }

    setLoadingSubmit(null)
  }

  const handleCanSave = useCallback(() => {
    let validation = true
    for (let i = 0; i < blocksToCheck.length; i++) {
      const block = blocksToCheck[i]
      if (!registerBlocks[block].canSave) {
        validation = false
      }
    }
    setCanSave(validation)
  }, [blocksToCheck, registerBlocks])

  // Montar e exibir modal de sucesso
  const onSubmitSuccess = useCallback(() => {
    const modalData = {
      feedbackType: 'success',
      feedbackMessage: isEdit ? 'Profissional atualizado com sucesso!' : 'Profissional adicionado com sucesso!',
      onSuccessClose: () => {
        dispatch(actions.hideModal())
        window.location.href = routes.DASHBOARD_PROFESSIONAL_LIST(role)
      }
    }
    dispatch(actions.showModal(modalTypes.FEEDBACK, modalData))
  }, [dispatch, isEdit, role])

  // Buscar informações do profissional
  useEffect(() => {
    if (isEdit)
      dispatch(actions.getPerson(slug, role))
  }, [dispatch, slug, role, isEdit])

  // Setar informações nos blocos do formulário
  useEffect(() => {
    if (isEdit && personGeneral.first_name && personProfile.id) {
      dispatch(actions.editBlocksData({
        person: personGeneral,
        personProfile: personProfile,
      }))
    }
  }, [dispatch, personGeneral, personProfile, isEdit])

  // Mostrar o formulário e fazer validação inicial
  useEffect(() => {
    if (isEdit && registerBlocks.blocksData.generalData.email && personGeneral.username === slug)
      setLoadingData(false)

    handleCanSave()
  }, [registerBlocks, isEdit, personGeneral, slug, handleCanSave])

  // Preparar mensagem de erro
  useEffect(() => {
    let message = null
    let messageDefault = 'Houve um problema para executar a ação requerida. Tente novamente.'

    if (errorList && errorList.fieldError && errorList.fieldError[0])
      message = errorList.fieldError[0].message || messageDefault

    if (errorList && errorList.sysError && errorList.sysError[0])
      message = errorList.sysError[0].message || messageDefault

    setError(message)
  }, [errorList])

  // Detectar sucesso e chamar modal
  useEffect(() => {
    if (submitSuccess) {
      onSubmitSuccess()
    }
  }, [submitSuccess, onSubmitSuccess])

  return (
    <Container>
      <Row align='center' justify={isDesktop ? 'space-between' : 'center'}
        margin={isDesktop ? '3.9rem 0 2.1rem' : '2.5rem 0 1.5rem'}>
        <Typography align='mcenter-dleft' Tag='h1' type='title'>
          {isEdit ? <Trans>Editar Profissional</Trans> : <Trans>Adicionar Profissional</Trans>}
        </Typography>
      </Row>

      {
        loadingData ?
          <Row>
            <Spinner height="250px" />
          </Row>
          :
          <Row>
            <form onSubmit={onSubmit}>
              <GeneralData role={role} />
              <DocumentsData role={role} />
              <MediaData role={role} />
              <BiographyData role={role} slug={slug} />
              <ProductsData role={role} />
              <SkillsData role={role} />
              <EducationData role={role} />
            </form>

            <Row>
              <LineSeparator lineMargin='3.95rem 0' />
              <Typography italic color="grey-dark">
                <Trans>*Campos de preenchimento obrigatório</Trans>
              </Typography>
            </Row>

            {
              error &&
              <Row margin='2rem 0 1.5rem'>
                <Toast icon="Info" message={error} variant="error" />
              </Row>
            }

            <Row margin='2.6rem 0'>
              <Column colWidth={6}>
                <Button
                  id="submit"
                  onButtonClick={() => onSubmit(submitTypes.PUBLISH)}
                  disabled={!canSave}
                  text={(<Trans>PUBLICAR PROFISSIONAL</Trans>)}
                  type="submit"
                  loading={loadingSubmit === submitTypes.PUBLISH}
                />
              </Column>
              <Column colWidth={6}>
                <Button
                  id="submit-schedule"
                  text={(<Trans>SALVAR PROFISSIONAL</Trans>)}
                  state="reverse"
                  onButtonClick={() => onSubmit(submitTypes.SAVE)}
                  disabled={!canSave}
                  loading={loadingSubmit === submitTypes.SAVE}
                />
              </Column>
            </Row>
          </Row>
      }
    </Container>
  )
}

export default NewProfessional